import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FormEvent, useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  captureInviteFromUrl,
  isAuthQuery,
  isLogoutQuery,
  isServiceKey,
  loginWithKey,
  logout,
  startLogin,
} from "../lib/auth";
import { hieraApi, type Me } from "../lib/api";
import { t, type Lang } from "../lib/i18n";
import { HIERA_WEB_VERSION } from "../version";
import { loadPageTabs, savePageTabs, splitVisibleTabs, tabLabel, upsertTab, type PageTab } from "../lib/pageTabs";
import { readSidebarCollapsed, writeSidebarCollapsed } from "../lib/prefs";
import { CommandPalette } from "./CommandPalette";
import { PageTabNoteContext } from "./pageTabContext";
import { SpaceNav } from "./SpaceNav";
import { canAdminRole } from "../lib/roles";
import { nextTheme, themeName, type ThemeId } from "../lib/theme";

type Props = {
  me: Me | null;
  lang: Lang;
  setLang: (l: Lang) => void;
  spaces: Array<{ id: string; title: string; slug: string }>;
  children: React.ReactNode;
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
  onAuthed: () => void;
};

function NavSvg({ children }: { children: React.ReactNode }) {
  return (
    <svg className="nav-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      {children}
    </svg>
  );
}

function IconHome() {
  return (
    <NavSvg>
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" strokeLinejoin="round" />
    </NavSvg>
  );
}

function IconClock() {
  return (
    <NavSvg>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.5l3 1.5" strokeLinecap="round" />
    </NavSvg>
  );
}

function IconStar() {
  return (
    <NavSvg>
      <path d="m12 3 2.4 5.4L20 9.2l-4 3.8.9 5.8L12 16.2 7.1 18.8 8 13 4 9.2l5.6-.8L12 3Z" strokeLinejoin="round" />
    </NavSvg>
  );
}

function IconDoc() {
  return (
    <NavSvg>
      <path d="M7 3.5h7l4 4V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" strokeLinejoin="round" />
      <path d="M14 3.5V8h4.5" strokeLinejoin="round" />
      <path d="M9 12h6M9 15.5h4" strokeLinecap="round" />
    </NavSvg>
  );
}

function IconSpace() {
  return (
    <NavSvg>
      <path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5v-9Z" strokeLinejoin="round" />
      <path d="M12 12v9M4 7.5 12 12l8-4.5" strokeLinejoin="round" />
    </NavSvg>
  );
}

function IconPlus() {
  return (
    <NavSvg>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </NavSvg>
  );
}

function IconImport() {
  return (
    <NavSvg>
      <path d="M12 3v10" strokeLinecap="round" />
      <path d="m8 9 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 18h14" strokeLinecap="round" />
    </NavSvg>
  );
}

function IconZip() {
  return (
    <NavSvg>
      <path d="M8 3.5h6l4 4V20a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" strokeLinejoin="round" />
      <path d="M14 3.5V8h4" strokeLinejoin="round" />
      <path d="M11 8v1M11 11v1M11 14v1" strokeLinecap="round" />
    </NavSvg>
  );
}

function IconHash() {
  return (
    <NavSvg>
      <path d="M9 4 7 20M17 4l-2 16M4 9h16M4 15h16" strokeLinecap="round" />
    </NavSvg>
  );
}

function IconInvite() {
  return (
    <NavSvg>
      <path d="M16 19v-1.2A3.8 3.8 0 0 0 12.2 14H7.8A3.8 3.8 0 0 0 4 17.8V19" strokeLinecap="round" />
      <circle cx="10" cy="8" r="3" />
      <path d="M18 8v6M15 11h6" strokeLinecap="round" />
    </NavSvg>
  );
}

const SIDEBAR_W_MIN = 180;
const SIDEBAR_W_MAX = 480;
const SIDEBAR_W_DEFAULT = 248;

function readSidebarWidth(): number {
  const raw = Number(localStorage.getItem("hiera.sidebarWidth") || SIDEBAR_W_DEFAULT);
  if (!Number.isFinite(raw)) return SIDEBAR_W_DEFAULT;
  return Math.min(SIDEBAR_W_MAX, Math.max(SIDEBAR_W_MIN, Math.round(raw)));
}

export function AppShell({
  me,
  lang,
  setLang,
  spaces,
  children,
  theme,
  setTheme,
  onAuthed,
}: Props) {
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreTabs, setMoreTabs] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [spacesOpen, setSpacesOpen] = useState(() => localStorage.getItem("hiera.spacesOpen") !== "0");
  const [toolsOpen, setToolsOpen] = useState(false);
  const [inviteHint, setInviteHint] = useState("");
  const [pageSpace, setPageSpace] = useState("");
  const [pageTabs, setPageTabs] = useState<PageTab[]>([]);
  const [activePageId, setActivePageId] = useState("");
  const [tabsUserId, setTabsUserId] = useState("");
  const [sideWidth, setSideWidth] = useState(readSidebarWidth);
  const [sideResizing, setSideResizing] = useState(false);
  const [collapsed, setCollapsedState] = useState(readSidebarCollapsed);

  function setCollapsed(next: boolean) {
    setCollapsedState(next);
    writeSidebarCollapsed(next);
  }

  function toggleCollapsed() {
    setCollapsedState((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }

  const restoredTabs = useRef(false);
  const spaceSticky = useRef("");
  const menuRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const mascotRef = useRef<HTMLButtonElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const importDirRef = useRef<HTMLInputElement>(null);
  const importZipRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();
  const loc = useLocation();
  const tab = new URLSearchParams(loc.search).get("tab");
  const spaceFromRoute = loc.pathname.match(/^\/s\/([^/]+)/)?.[1] || "";
  const pageFromRoute = loc.pathname.match(/^\/p\/([^/]+)/)?.[1] || "";
  const tabSpace =
    pageTabs.find((row) => row.id === (pageFromRoute || activePageId))?.spaceId || "";
  const resolvedSpace = spaceFromRoute || pageSpace || tabSpace;
  if (resolvedSpace) spaceSticky.current = resolvedSpace;
  const activeSpace = resolvedSpace || (pageFromRoute ? spaceSticky.current : "");
  const activeTitle = spaces.find((s) => s.id === activeSpace)?.title || "";

  async function importBundle(list: File[]) {
    if (!list.length) return;
    const folder = list[0]?.webkitRelativePath?.split("/")[0] || "";
    const title = window.prompt(t(lang, "newSpace"), folder) || folder || "Imported";
    try {
      const { space } = await hieraApi.importDocmost(list, title);
      onAuthed();
      nav(`/s/${space.id}`);
    } catch {
      window.alert(t(lang, "importFailed"));
    }
  }

  const noteTab = useCallback((tab: PageTab) => {
    setPageTabs((prev) => upsertTab(prev, tab));
    setActivePageId(tab.id);
  }, []);

  function closeTab(id: string) {
    const idx = pageTabs.findIndex((row) => row.id === id);
    const next = pageTabs.filter((row) => row.id !== id);
    setPageTabs(next);
    if (pageFromRoute !== id) {
      if (activePageId === id) setActivePageId(next.at(-1)?.id || "");
      return;
    }
    const neighbor = next[idx] || next[idx - 1];
    setActivePageId(neighbor?.id || "");
    nav(neighbor ? `/p/${neighbor.id}` : activeSpace ? `/s/${activeSpace}` : "/");
  }

  useEffect(() => {
    const { token, fromUrl } = captureInviteFromUrl();
    if (!fromUrl || !token || me) return;
    void startLogin("/");
  }, [me]);

  useEffect(() => {
    if (!me) {
      setPageTabs([]);
      setActivePageId("");
      setTabsUserId("");
      restoredTabs.current = false;
      return;
    }
    const stored = loadPageTabs(me.id);
    setPageTabs(stored.tabs);
    setActivePageId(stored.activeId);
    setTabsUserId(me.id);
    restoredTabs.current = false;
  }, [me?.id]);

  useEffect(() => {
    if (!me || tabsUserId !== me.id) return;
    savePageTabs(me.id, pageTabs, activePageId);
  }, [me, tabsUserId, pageTabs, activePageId]);

  useEffect(() => {
    if (!me || tabsUserId !== me.id || restoredTabs.current) return;
    restoredTabs.current = true;
    if (pageFromRoute || loc.pathname !== "/") return;
    if (activePageId) nav(`/p/${activePageId}`, { replace: true });
  }, [me, tabsUserId, pageFromRoute, loc.pathname, activePageId, nav]);

  useEffect(() => {
    if (!pageFromRoute) {
      setPageSpace("");
      return;
    }
    let dead = false;
    hieraApi.page(pageFromRoute).then((res) => {
      if (dead) return;
      setPageSpace(res.page.space_id);
      setActivePageId(res.page.id);
      setPageTabs((prev) => (
        upsertTab(prev, {
          id: res.page.id,
          title: res.page.title || "Untitled",
          spaceId: res.page.space_id,
        })
      ));
    }).catch((err: unknown) => {
      if (dead) return;
      if ((err as { status?: number }).status !== 404) return;
      let neighborId = "";
      setPageTabs((prev) => {
        const idx = prev.findIndex((row) => row.id === pageFromRoute);
        const next = prev.filter((row) => row.id !== pageFromRoute);
        neighborId = (next[idx] || next[idx - 1])?.id || "";
        return next;
      });
      setActivePageId(neighborId);
      nav(neighborId ? `/p/${neighborId}` : "/", { replace: true });
    });
    return () => {
      dead = true;
    };
  }, [pageFromRoute, nav]);

  useEffect(() => {
    localStorage.setItem("hiera.sidebarWidth", String(sideWidth));
  }, [sideWidth]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setPalette(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        toggleCollapsed();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onSidebarResizeStart(e: ReactPointerEvent<HTMLDivElement>) {
    if (collapsed) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = sideWidth;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    setSideResizing(true);

    function onMove(ev: PointerEvent) {
      const next = Math.min(SIDEBAR_W_MAX, Math.max(SIDEBAR_W_MIN, Math.round(startW + (ev.clientX - startX))));
      setSideWidth(next);
    }
    function onUp(ev: PointerEvent) {
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("pointercancel", onUp);
      setSideResizing(false);
    }
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
    target.addEventListener("pointercancel", onUp);
  }

  useEffect(() => {
    if (!langMenuOpen) return;
    function onPointer(e: MouseEvent) {
      if (!langRef.current?.contains(e.target as Node)) setLangMenuOpen(false);
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [langMenuOpen]);

  useEffect(() => {
    if (!moreTabs) return;
    function onPointer(e: MouseEvent) {
      if (!moreRef.current?.contains(e.target as Node)) setMoreTabs(false);
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [moreTabs]);

  useEffect(() => {
    if (!menuOpen) return;
    function onPointer(e: MouseEvent) {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (mascotRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [menuOpen]);

  useEffect(() => {
    if (!toolsOpen) return;
    function onPointer(e: MouseEvent) {
      if (!toolsRef.current?.contains(e.target as Node)) setToolsOpen(false);
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [toolsOpen]);

  async function onSearch(e: FormEvent) {
    e.preventDefault();
    const text = q.trim();
    if (isServiceKey(text)) {
      setQ("");
      try {
        await loginWithKey(text);
        window.location.assign("/");
      } catch {
        window.alert(t(lang, "keyRejected"));
      }
      return;
    }
    // Auth trigger stays client-only — never put into URL.
    if (isAuthQuery(text)) {
      setQ("");
      await startLogin("/");
      return;
    }
    if (me && isLogoutQuery(text)) {
      setQ("");
      await logout();
      window.location.reload();
      return;
    }
    if (!me) return;
    setPalette(true);
  }

  const tabView = splitVisibleTabs(pageTabs, pageFromRoute || activePageId);

  return (
    <div
      className={`shell ${collapsed ? "collapsed" : ""} ${sideResizing ? "is-resizing" : ""} ${me ? "" : "locked"}`}
      style={{ ["--sidebar-w" as string]: `${sideWidth}px` }}
    >
      <header className="topbar">
        <div className="topbar-toolbar">
          <button
            ref={mascotRef}
            type="button"
            className="mascot"
            aria-label="Hiera"
            onClick={() => nav("/")}
            onContextMenu={(e) => {
              e.preventDefault();
              if (!me) return;
              setMenuOpen(true);
            }}
          >
            <img src={`/mascot.png?v=${HIERA_WEB_VERSION}`} alt="" width={128} height={128} />
          </button>
          <form className="head-search" onSubmit={onSearch} role="search">
            <svg className="head-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.2-3.2" strokeLinecap="round" />
            </svg>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t(lang, "search")}
              aria-label={t(lang, "search")}
              type="search"
              autoComplete="off"
            />
            <kbd className="head-search-kbd">Ctrl Shift P</kbd>
          </form>
          <div className="lang-switch" ref={langRef}>
            <button
              type="button"
              className="lang-current"
              onClick={() => setLang(lang === "ru" ? "en" : "ru")}
            >
              {lang.toUpperCase()}
            </button>
            <button
              type="button"
              className="lang-toggle"
              aria-label="Language"
              onClick={() => setLangMenuOpen((open) => !open)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {langMenuOpen ? (
              <div className="lang-menu">
                <button type="button" aria-current={lang === "ru"} onClick={() => { setLang("ru"); setLangMenuOpen(false); }}>
                  Русский
                </button>
                <button type="button" aria-current={lang === "en"} onClick={() => { setLang("en"); setLangMenuOpen(false); }}>
                  English
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {menuOpen && me ? (
        <div
          className="ctx-menu"
          ref={menuRef}
          style={{
            top: (mascotRef.current?.getBoundingClientRect().bottom ?? 96) + 6,
            left: mascotRef.current?.getBoundingClientRect().left ?? 16,
          }}
        >
          <div className="ctx-item muted profile-row">
            <img src={`/mascot.png?v=${HIERA_WEB_VERSION}`} alt="" width={36} height={36} />
            <span>
              Hiera {t(lang, "version")} {HIERA_WEB_VERSION}
            </span>
          </div>
          <button type="button" className="ctx-item" onClick={() => { setMenuOpen(false); nav("/me"); }}>
            {t(lang, "myProfile")}
          </button>
          <button type="button" className="ctx-item" onClick={() => { setMenuOpen(false); nav("/settings"); }}>
            {t(lang, "mySettings")}
          </button>
          <button
            type="button"
            className="ctx-item"
            onClick={() => setTheme(nextTheme(theme))}
          >
            {t(lang, "theme")}: {themeName(theme)}
          </button>
          <button type="button" className="ctx-item" onClick={() => { setMenuOpen(false); nav("/people"); }}>
            {t(lang, "members")}
          </button>
          {canAdminRole(me.globalRole) ? (
            <button type="button" className="ctx-item" onClick={() => { setMenuOpen(false); nav("/admin"); }}>
              {t(lang, "admin")}
            </button>
          ) : null}
          <button
            type="button"
            className="ctx-item"
            onClick={async () => {
              setMenuOpen(false);
              await logout();
              window.location.reload();
            }}
          >
            {t(lang, "logout")}
          </button>
        </div>
      ) : null}

      {me ? (
        <PageTabNoteContext.Provider value={noteTab}>
        {pageTabs.length ? <div className="page-tabs" role="tablist">
          {tabView.shown.map((row) => {
            const open = row.id === pageFromRoute;
            return (
              <div
                key={row.id}
                className={`page-tab${open ? " is-on" : ""}`}
                role="tab"
                aria-selected={open}
                onAuxClick={(e) => {
                  if (e.button === 1) closeTab(row.id);
                }}
              >
                <button
                  type="button"
                  className="page-tab-label"
                  title={row.title || "Untitled"}
                  onClick={() => {
                    setActivePageId(row.id);
                    nav(`/p/${row.id}`);
                  }}
                >
                  {tabLabel(row.title)}
                </button>
                <button
                  type="button"
                  className="page-tab-x"
                  aria-label={t(lang, "closeTab")}
                  onClick={() => closeTab(row.id)}
                >
                  ×
                </button>
              </div>
            );
          })}
          {tabView.rest.length ? (
            <div className="page-tab-more" ref={moreRef}>
              <button
                type="button"
                className="page-tab-more-btn"
                aria-label={t(lang, "moreTabs")}
                aria-expanded={moreTabs}
                onClick={() => setMoreTabs((open) => !open)}
              >
                …
              </button>
              {moreTabs ? (
                <div className="page-tab-more-menu">
                  {tabView.rest.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => {
                        setMoreTabs(false);
                        setActivePageId(row.id);
                        nav(`/p/${row.id}`);
                      }}
                    >
                      {row.title || "Untitled"}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {activeSpace ? (
            <button
              type="button"
              className="page-tab-add"
              aria-label={t(lang, "newPage")}
              onClick={async () => {
                const { page } = await hieraApi.createPage(activeSpace, { title: "Untitled" });
                const id = String(page.id || "");
                if (id) nav(`/p/${id}`);
              }}
            >
              +
            </button>
          ) : null}
        </div> : null}
        <div className="body">
          <div className="sidebar-rail">
            <aside className="sidebar">
              <div className="sidebar-scroll">
                <div className="space-switch-wrap">
                  <button type="button" className="space-switch" onClick={() => setSwitchOpen((open) => !open)}>
                    <span className="space-switch-name">
                      <span>{activeSpace ? activeTitle || "…" : "Hiera"}</span>
                    </span>
                    <span aria-hidden="true">▾</span>
                  </button>
                  {switchOpen ? (
                    <div className="space-menu">
                      <button
                        type="button"
                        onClick={() => {
                          setSwitchOpen(false);
                          nav("/");
                        }}
                      >
                        {t(lang, "allSpaces")}
                      </button>
                      {spaces.map((s) => (
                        <Link key={s.id} to={`/s/${s.id}`} onClick={() => setSwitchOpen(false)}>
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
                {activeSpace ? (
                  <SpaceNav lang={lang} spaceId={activeSpace} pageId={pageFromRoute} onChanged={onAuthed} />
                ) : pageFromRoute ? (
                  <div className="side-loading" aria-busy="true" />
                ) : (
                  <>
                    <nav>
                      <NavLink to="/" className={() => (loc.pathname === "/" && !tab ? "is-active" : "")}>
                        <IconHome />
                        <span className="nav-label">{t(lang, "home")}</span>
                      </NavLink>
                      <NavLink to="/?tab=recent" className={() => (tab === "recent" ? "is-active" : "")}>
                        <IconClock />
                        <span className="nav-label">{t(lang, "recent")}</span>
                      </NavLink>
                      <NavLink to="/?tab=favorites" className={() => (tab === "favorites" ? "is-active" : "")}>
                        <IconStar />
                        <span className="nav-label">{t(lang, "favorites")}</span>
                      </NavLink>
                      <NavLink to="/templates" className={({ isActive }) => (isActive ? "is-active" : "")}>
                        <IconDoc />
                        <span className="nav-label">{t(lang, "templates")}</span>
                      </NavLink>
                    </nav>
                    <div className="spaces-fold">
                      <button
                        type="button"
                        className="spaces-fold-btn"
                        aria-expanded={spacesOpen}
                        onClick={() => {
                          const next = !spacesOpen;
                          setSpacesOpen(next);
                          localStorage.setItem("hiera.spacesOpen", next ? "1" : "0");
                        }}
                      >
                        <span className="nav-label">{t(lang, "spaces")}</span>
                        <svg className="chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                      {spacesOpen ? (
                        <div className="spaces-fold-body">
                          <ul className="space-list">
                            {spaces.map((s) => (
                              <li key={s.id}>
                                <NavLink to={`/s/${s.id}`}>
                                  <IconSpace />
                                  <span className="nav-label">{s.title}</span>
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
              {!activeSpace && !pageFromRoute ? (
                <div className="side-dock side-pin">
                  <button
                    type="button"
                    className="side-dock-btn side-dock-btn--accent"
                    title={t(lang, "create")}
                    aria-label={t(lang, "create")}
                    onClick={async () => {
                      const title = window.prompt(t(lang, "newSpace"));
                      if (!title) return;
                      const { space } = await hieraApi.createSpace(title);
                      onAuthed();
                      nav(`/s/${space.id}`);
                    }}
                  >
                    <IconPlus />
                  </button>
                  <button
                    type="button"
                    className="side-dock-btn"
                    title={t(lang, "invitePeople")}
                    aria-label={t(lang, "invitePeople")}
                    onClick={async () => {
                      try {
                        const { token } = await hieraApi.createInvite();
                        const url = `${window.location.origin}/?invite=${encodeURIComponent(token)}`;
                        await navigator.clipboard.writeText(url);
                        setInviteHint(t(lang, "inviteCopied"));
                      } catch {
                        setInviteHint(t(lang, "inviteFailed"));
                      }
                      window.setTimeout(() => setInviteHint(""), 2400);
                    }}
                  >
                    <IconInvite />
                  </button>
                  <div className="side-tools-wrap" ref={toolsRef}>
                    <button
                      type="button"
                      className={`side-dock-btn${toolsOpen ? " is-on" : ""}`}
                      title="#"
                      aria-label="#"
                      aria-expanded={toolsOpen}
                      aria-haspopup="menu"
                      onClick={(e) => {
                        e.stopPropagation();
                        setToolsOpen((v) => !v);
                      }}
                    >
                      <IconHash />
                    </button>
                    {toolsOpen ? (
                      <div
                        className="side-tools-menu"
                        role="menu"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setToolsOpen(false);
                            importDirRef.current?.click();
                          }}
                        >
                          <IconImport />
                          <span>{t(lang, "importDocmost")}</span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setToolsOpen(false);
                            importZipRef.current?.click();
                          }}
                        >
                          <IconZip />
                          <span>{t(lang, "importZip")}</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {inviteHint ? <span className="side-dock-hint">{inviteHint}</span> : null}
                  <input
                    ref={importDirRef}
                    className="sr-only"
                    type="file"
                    tabIndex={-1}
                    aria-hidden="true"
                    multiple
                    // Directory export: docmost-metadata.json + nested markdown.
                    {...{ webkitdirectory: "", directory: "" }}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      const list = input.files ? Array.from(input.files) : [];
                      input.value = "";
                      void importBundle(list);
                    }}
                  />
                  <input
                    ref={importZipRef}
                    className="sr-only"
                    type="file"
                    tabIndex={-1}
                    aria-hidden="true"
                    accept=".zip,application/zip"
                    onChange={(e) => {
                      const input = e.currentTarget;
                      const list = input.files ? Array.from(input.files) : [];
                      input.value = "";
                      void importBundle(list);
                    }}
                  />
                </div>
              ) : null}
            </aside>
            <div
              className="sidebar-resize"
              role="separator"
              aria-orientation="vertical"
              aria-label={t(lang, "resizeSidebar")}
              onPointerDown={onSidebarResizeStart}
            />
            <button
              type="button"
              className="sidebar-fold"
              title={collapsed ? t(lang, "expandSidebar") : t(lang, "collapseSidebar")}
              aria-label={collapsed ? t(lang, "expandSidebar") : t(lang, "collapseSidebar")}
              aria-expanded={!collapsed}
              onClick={() => toggleCollapsed()}
            >
              {collapsed ? "<" : ">"}
            </button>
          </div>
          <main className="main">
            <div className="main-scroll">{children}</div>
          </main>
        </div>
        </PageTabNoteContext.Provider>
      ) : (
        <div className="locked-hint">Hiera</div>
      )}

      {palette && me ? (
        <CommandPalette
          lang={lang}
          onClose={() => setPalette(false)}
          initialQuery={q}
          onOpen={(path) => {
            setPalette(false);
            nav(path);
          }}
        />
      ) : null}
    </div>
  );
}

export function useFlashBus(_setFlash: (s: string) => void) {
  return _setFlash;
}
